/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import { AlarmDevice, AlarmDevice$inboundSchema } from "./alarmdevice.js";
import { AlarmSnapshot, AlarmSnapshot$inboundSchema } from "./alarmsnapshot.js";
import { UserInfo, UserInfo$inboundSchema } from "./userinfo.js";

/**
 * Severity of the alarm.
 */
export const Severity = {
  Critical: "critical",
  Warning: "warning",
  Caution: "caution",
} as const;
/**
 * Severity of the alarm.
 */
export type Severity = OpenEnum<typeof Severity>;

/**
 * Alarm activation information
 */
export type AlarmActivation = {
  /**
   * RFC3339 formatted time in UTC
   */
  acknowledgedAt?: Date | undefined;
  /**
   * User information about a User in the System.
   */
  acknowledgedBy?: UserInfo | undefined;
  /**
   * RFC3339 formatted time in UTC
   */
  activatedAt: Date;
  /**
   * Universally unique identifier
   */
  activationId: string;
  /**
   * Name of the alarm group
   */
  alarmGroup?: string | undefined;
  /**
   * RFC3339 formatted time in UTC
   */
  clearedAt?: Date | undefined;
  /**
   * User information about a User in the System.
   */
  clearedBy?: UserInfo | undefined;
  /**
   * Control points that are relevant to the alarm expression
   */
  controlPoints?: Array<AlarmSnapshot> | undefined;
  /**
   * Metadata for devices associated with the alarm at shared.Time of activation
   */
  device?: AlarmDevice | undefined;
  /**
   * Message of the alarm
   */
  message: string;
  /**
   * RFC3339 formatted time in UTC
   */
  resolvedAt?: Date | undefined;
  /**
   * Threshold settings that were exceeded at the moment of activation
   */
  settings?: Array<AlarmSnapshot> | undefined;
  /**
   * Severity of the alarm.
   */
  severity: Severity;
};

/** @internal */
export const Severity$inboundSchema: z.ZodType<
  Severity,
  z.ZodTypeDef,
  unknown
> = openEnums.inboundSchema(Severity);

/** @internal */
export const AlarmActivation$inboundSchema: z.ZodType<
  AlarmActivation,
  z.ZodTypeDef,
  unknown
> = z.object({
  acknowledged_at: types.optional(types.date()),
  acknowledged_by: types.optional(UserInfo$inboundSchema),
  activated_at: types.date(),
  activation_id: types.string(),
  alarm_group: types.optional(types.string()),
  cleared_at: types.optional(types.date()),
  cleared_by: types.optional(UserInfo$inboundSchema),
  control_points: types.optional(z.array(AlarmSnapshot$inboundSchema)),
  device: types.optional(AlarmDevice$inboundSchema),
  message: types.string(),
  resolved_at: types.optional(types.date()),
  settings: types.optional(z.array(AlarmSnapshot$inboundSchema)),
  severity: Severity$inboundSchema,
}).transform((v) => {
  return remap$(v, {
    "acknowledged_at": "acknowledgedAt",
    "acknowledged_by": "acknowledgedBy",
    "activated_at": "activatedAt",
    "activation_id": "activationId",
    "alarm_group": "alarmGroup",
    "cleared_at": "clearedAt",
    "cleared_by": "clearedBy",
    "control_points": "controlPoints",
    "resolved_at": "resolvedAt",
  });
});

export function alarmActivationFromJSON(
  jsonString: string,
): SafeParseResult<AlarmActivation, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => AlarmActivation$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'AlarmActivation' from JSON`,
  );
}
